import { Divider } from '@mui/material';
import { ReactElement, useCallback, useMemo } from 'react';
import { FormattedMessage, formatTranslation } from 'intl';
import { DialogTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { useCurrentUserInProjectModel } from 'store/user';
import { logError } from 'setup/logger/logError';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { getAccessLevelOnDropdownView } from 'shared/domain/issue/mapping/accessLevel/toOnView/getAccessLevelOnDropdownView';
import { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import { HashMap } from 'shared/types/commonView';
import { getAccessLevelIcon } from './getIcon';
import { ManagementSelect } from './select';
import { useStyles } from './style';
import {
  MicroManagementProps,
  MicroManagementUserListProps,
} from './types';

export function MicroManagement({
  users,
  usersWithoutEdit,
  issueUserAccesses,
  siteId,
  processId,
  onChange,
}: MicroManagementProps): ReactElement {
  const intl = useIntl();
  const classes = useStyles();
  const accessArray = issueUserAccesses;
  const userAccessByUserId = useMemo(() => {
    return accessArray.reduce<HashMap<UserAccess>>((record, access) => {
      record[access.accessingUser] = access;
      return record;
    }, {});
  }, [accessArray]);
  const submitter = useCurrentUserInProjectModel();

  const onSelectChange = useCallback(
    (
      newAccessLevel: AccessLevelModel,
      currentAccessLevel: AccessLevelModel,
      userId: string,
      userLabel: string
    ): void => {
      onChange(
        newAccessLevel,
        currentAccessLevel,
        userId,
        userLabel,
        accessArray
      );
    },
    [onChange, userAccessByUserId]
  );

  return (
    <div className={classes.microManagement}>
      <div className={classes.dialogDescription}>
        <FormattedMessage id={DialogTranslationKeys.userAccessChangeIssueMessage} />
      </div>
      <Divider />
      <p className={classes.px}>
        {formatTranslation(intl, DialogTranslationKeys.userAccessGrantAccessMessage,)}
      </p>
      {usersWithoutEdit.map((userWithoutEdit) => {
        const foundUser = users.find(
          (user) => user._id === userWithoutEdit._id
        );
        if (!foundUser) {
          logError(
            'Mentioned user cannot be found in users array',
            userWithoutEdit
          );
          return null;
        }

        const accessLevelOnDropdownView = getAccessLevelOnDropdownView({
          issueUserAccesses: issueUserAccesses,
          submitter,
          targetUser: foundUser,
          siteId: siteId,
          processId: processId,
        });

        return (
          <MicroManagementUser
            key={userWithoutEdit._id}
            userId={foundUser._id}
            userLabel={foundUser.label}
            userRole={foundUser.role}
            accessLevelOnDropdownView={accessLevelOnDropdownView}
            onChange={onSelectChange}
          />
        );
      })}
    </div>
  );
}

function MicroManagementUser({
  userId,
  userLabel,
  userRole,
  accessLevelOnDropdownView,
  onChange,
}: MicroManagementUserListProps): ReactElement | null {
  const classes = useStyles();

  return (
    <div className={classes.usersList}>
      <ManagementSelect
        userId={userId}
        userLabel={userLabel}
        userRole={userRole}
        currentAccess={accessLevelOnDropdownView.value}
        onChange={onChange}
        disabled={accessLevelOnDropdownView.disabled}
        disabledReasons={accessLevelOnDropdownView.disabledReasons}
        explanationLabel={accessLevelOnDropdownView.explanationLabel}
        Icon={getAccessLevelIcon(
          accessLevelOnDropdownView.value,
          accessLevelOnDropdownView.disabled
        )}
      />
      <span>{userLabel}</span>
    </div>
  );
}
